import { resizeManager } from './resizeManager'

class SearchToggle {
  private toggleButtons: NodeListOf<HTMLElement>
  private searchForm: HTMLElement | null = null
  private isOpen: boolean = false
  private unsubscribe: (() => void) | null = null

  constructor() {
    this.toggleButtons = document.querySelectorAll('.js-toggle-search')
    this.searchForm = document.querySelector('.js-search-form')

    if (!this.toggleButtons.length || !this.searchForm) {
      return
    }

    this.init()
    this.unsubscribe = resizeManager.subscribe('search', (isSmall) => {
      if (isSmall && this.isOpen) {
        this.close()
      }
    })
  }

  private init(): void {
    if (!this.searchForm) {
      return
    }

    this.searchForm.style.height = '0'
    this.searchForm.style.overflow = 'hidden'
    this.searchForm.style.opacity = '0'

    this.setupEventListeners()
  }

  private setupEventListeners(): void {
    this.toggleButtons.forEach((button) => {
      button.addEventListener('click', () => this.toggle())
      button.addEventListener('keydown', (e: KeyboardEvent) => this.onKeyDown(e))
    })
    document.addEventListener('click', (e: MouseEvent) => this.handleOutsideClick(e))
  }

  private toggle(): void {
    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  private open(): void {
    if (!this.searchForm) {return}

    this.isOpen = true

    this.toggleButtons.forEach((button) => {
      button.setAttribute('aria-expanded', 'true')
      button.setAttribute('aria-label', 'Stäng sökformulär')
      this.updateButtonIcon(button, true)
    })

    this.enter(this.searchForm)

    requestAnimationFrame(() => {
      const searchInput = this.searchForm?.querySelector<HTMLInputElement>('.search-input')
      if (searchInput) {searchInput.focus()}
    })
  }

  private close(): void {
    if (!this.searchForm) {return}

    this.isOpen = false

    this.toggleButtons.forEach((button) => {
      button.setAttribute('aria-expanded', 'false')
      button.setAttribute('aria-label', 'Öppna sökformulär')
      this.updateButtonIcon(button, false)

      if (document.activeElement === button) {button.blur()}
    })

    this.leave(this.searchForm)
  }

  private updateButtonIcon(button: HTMLElement, isOpen: boolean): void {
    const searchIcon = button.querySelector('.js-search-icon')
    const closeIcon = button.querySelector('.js-close-icon')

    if (searchIcon && closeIcon) {
      if (isOpen) {
        searchIcon.classList.add('hidden')
        closeIcon.classList.remove('hidden')
      } else {
        searchIcon.classList.remove('hidden')
        closeIcon.classList.add('hidden')
      }
    }
  }

  private enter(el: HTMLElement): void {
    el.style.height = '0px'
    el.style.opacity = '0'
    el.style.overflow = 'hidden'

    requestAnimationFrame(() => {
      el.style.transition = 'height 0.3s ease-in-out, opacity 0.3s ease-in-out'
      el.style.height = '150px'
      el.style.opacity = '1'

      setTimeout(() => {
        el.style.overflow = ''
      }, 300)
    })
  }

  private leave(el: HTMLElement): void {
    el.style.height = '150px'
    el.style.opacity = '1'
    el.style.overflow = 'hidden'

    requestAnimationFrame(() => {
      el.style.transition = 'height 0.3s ease-in-out, opacity 0.3s ease-in-out'
      el.style.height = '0px'
      el.style.opacity = '0'

      setTimeout(() => {
        el.style.overflow = 'hidden'
      }, 300)
    })
  }

  private handleOutsideClick(event: MouseEvent): void {
    if (!this.isOpen || !this.searchForm) {return}

    const target = event.target as Node
    const isClickInSearchForm = this.searchForm.contains(target)
    const isClickOnToggle = Array.from(this.toggleButtons).some((button) => button.contains(target))

    if (!isClickInSearchForm && !isClickOnToggle) {
      this.close()
    }
  }

  private onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.isOpen) {
      this.close()
    }
  }

  public destroy(): void {
    this.toggleButtons.forEach((button) => {
      button.removeEventListener('click', () => this.toggle())
      button.removeEventListener('keydown', (e: KeyboardEvent) => this.onKeyDown(e))
    })
    document.removeEventListener('click', (e: MouseEvent) => this.handleOutsideClick(e))
    this.unsubscribe?.()
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new SearchToggle())
} else {
  new SearchToggle()
}

export default SearchToggle
